// One post with its comments (app flow §5). Not found and not visible look the same. The quote is the
// largest text; on wide screens the comments sit in the margin beside it like marginalia, and on
// phones the comment box is a raised card at the foot of the conversation.
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Button, IconButton, LinkButton } from "../../components/Button";
import { EmptyState } from "../../components/Empty";
import { MentionTextarea } from "../../components/MentionTextarea";
import { Post } from "../../components/Post";
import { RichText } from "../../components/RichText";
import { SectionHeader } from "../../components/SectionHeader";
import { PostSkeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import ui from "../../components/ui.module.css";
import { useAddComment, useComments, useDeleteComment, usePost } from "../../data/queries";
import { LIMITS } from "../../data/types";
import { longTime, shortTime } from "../../lib/time";
import { useTitle } from "../../lib/useTitle";
import { AnimatedList, morphFrom } from "../../motion";
import s from "./p.module.css";

export const Route = createFileRoute("/_app/p/$postId")({ component: PostPage });

const MAX = LIMITS.comment;

function PostPage() {
  const { postId } = Route.useParams();
  const router = useRouter();
  const { data: post, isPending } = usePost(postId);
  const { data: comments } = useComments(postId);
  const add = useAddComment(postId);
  const remove = useDeleteComment(postId);
  const toast = useToast();
  // Deleting a comment waits five seconds behind an Undo, then goes to the server.
  const [hidden, setHidden] = useState<string[]>([]);
  const deleteComment = (id: string) => {
    setHidden((h) => [...h, id]);
    const t = setTimeout(() => remove.mutate(id), 5000);
    toast("Comment deleted", () => {
      clearTimeout(t);
      setHidden((h) => h.filter((x) => x !== id));
    });
  };
  const [text, setText] = useState("");
  useTitle(post ? `${post.author.displayName}’s post` : "Post");

  if (isPending)
    return (
      <div className={ui.page}>
        <PostSkeleton count={1} />
      </div>
    );
  if (!post)
    return (
      <div className={ui.page}>
        <EmptyState title="This post isn’t available." body="It may have been deleted, or it was shared only with friends.">
          <LinkButton to="/home" variant="secondary">
            Go home
          </LinkButton>
        </EmptyState>
      </div>
    );

  const shown = comments?.filter((c) => !hidden.includes(c.id)) ?? [];
  const count = post.commentCount;

  return (
    <div className={s.page}>
      <div className={`${ui.pageHead} ${s.head}`}>
        <IconButton label="Back" icon="back" look="outline" onClick={() => router.history.back()} />
        <h1 className={s.heading}>Post</h1>
      </div>
      <div className={s.layout}>
        <div className={s.main}>
          <Post post={post} variant="detail" />
        </div>

        <section aria-labelledby="comments-head" className={s.comments}>
          <SectionHeader id="comments-head" title={count === 0 ? "No comments yet" : count === 1 ? "1 comment" : `${count} comments`} className={s.commentsHead} />
          {shown.length > 0 && (
            <AnimatedList className={s.list}>
              {shown.map((c) => (
                <li key={c.id} className={s.comment}>
                  <Link to="/@{$handle}" params={{ handle: c.author.handle }} aria-hidden="true" tabIndex={-1} className={s.face} onClick={morphFrom("avatar", c.author.id)}>
                    <Avatar user={c.author} size={32} morph />
                  </Link>
                  <div className={s.words}>
                    <p className={s.by}>
                      <Link to="/@{$handle}" params={{ handle: c.author.handle }} className={s.name}>
                        {c.author.displayName}
                      </Link>{" "}
                      <time className={s.time} dateTime={c.createdAt} title={longTime(c.createdAt)}>
                        {shortTime(c.createdAt)}
                      </time>
                    </p>
                    <p className={s.text}>
                      <RichText text={c.body} />
                    </p>
                  </div>
                  {c.canDelete && (
                    <Button variant="ghost" size="sm" className={s.delete} onClick={() => deleteComment(c.id)} aria-label={`Delete comment by ${c.author.displayName}`}>
                      Delete
                    </Button>
                  )}
                </li>
              ))}
            </AnimatedList>
          )}
          <form
            className={s.form}
            onSubmit={(e) => {
              e.preventDefault();
              const body = text.trim();
              if (body) add.mutate(body, { onSuccess: () => setText("") });
            }}
          >
            <label htmlFor="comment" className="visually-hidden">
              Add a comment
            </label>
            <MentionTextarea
              id="comment"
              className={`${ui.area} ${s.area}`}
              placeholder={count === 0 ? "Be the first to write in the margin. Type @ to mention a friend" : "Add a comment. Type @ to mention a friend"}
              rows={2}
              maxLength={MAX}
              value={text}
              onValueChange={setText}
            />
            {add.isError && (
              <p className={ui.error} role="alert">
                {add.error.message}
              </p>
            )}
            <div className={s.formFoot}>
              <span className={s.left} aria-live="polite">
                {text.length > MAX - 100 ? `${MAX - text.length} characters left` : ""}
              </span>
              <Button type="submit" variant="primary" size="sm" busy={add.isPending} disabled={!text.trim() || add.isPending}>
                {add.isPending ? "Posting…" : "Post"}
              </Button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
